import React, { useState, useEffect } from 'react';
import { Search, X, User } from 'lucide-react';
import api from '../services/api';

const UserSelector = ({ isOpen, onClose, onSelect, selectedUserId }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user => {
        // Suportar ambos os formatos: user.usuario ou user direto
        const usuarioData = user.usuario || user;
        if (!usuarioData) return false;
        
        const nome = usuarioData.usuario_nome || '';
        const cpf = usuarioData.usuario_cpf || '';
        
        return nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
               cpf.includes(searchTerm);
      });
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Buscar usuários de todos os condomínios
      const response = await api.get('/usuarios?limit=1000');
      setUsers(response.data.data.usuarios || []);
      setFilteredUsers(response.data.data.usuarios || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (user) => {
    onSelect(user);
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Selecionar Usuário
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou CPF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Carregando usuários...</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário disponível'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((userCondominio) => {
                // Suportar ambos os formatos: userCondominio.usuario ou userCondominio direto
                const user = userCondominio.usuario || userCondominio;
                if (!user || !user.usuario_id) {
                  console.warn('Usuário inválido no UserSelector:', userCondominio);
                  return null;
                }
                
                const isSelected = selectedUserId === user.usuario_id;
                
                return (
                  <div
                    key={userCondominio.usuario_condominio_id || user.usuario_id}
                    onClick={() => handleSelect(userCondominio)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <User size={20} className="text-gray-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.usuario_nome || 'N/A'}
                          </p>
                          {isSelected && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Selecionado
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {user.usuario_email || 'N/A'}
                        </p>
                        {user.usuario_cpf && (
                          <p className="text-sm text-gray-500 truncate">
                            CPF: {user.usuario_cpf}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 truncate">
                          Condomínio: {userCondominio.condominio?.condominio_nome || 'N/A'}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          userCondominio.tipo_acesso === 'administrador' ? 'bg-purple-100 text-purple-800' :
                          userCondominio.tipo_acesso === 'sindico' ? 'bg-blue-100 text-blue-800' :
                          userCondominio.tipo_acesso === 'subsindico' ? 'bg-indigo-100 text-indigo-800' :
                          userCondominio.tipo_acesso === 'porteiro' ? 'bg-yellow-100 text-yellow-800' :
                          userCondominio.tipo_acesso === 'morador' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {userCondominio.tipo_acesso || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSelector;
